import React, { useEffect, useRef, useState } from 'react';
import Header from '@/components/MobileApproval/Header';

import {
  BannerLayout,
  Container,
  HeaderContentContainer,
  HeaderLayout,
  SubHeaderContentContainer,
} from '@/components/MobileApproval/styles';
import HeaderContent from '@/components/MobileApproval/HeaderContent';
import { TotalApprovalParams } from '@/types/api';
import ApprovalWirte from '@/components/MobileApproval/ApprovalWirte';
import { useAtom, useAtomValue } from 'jotai';
import { approvalState, MyInfoState } from '@/recoil/atom';
import { useTranslation } from 'react-i18next';
import { useSearchApproval } from '@/hooks/apiHooks';
import { rnPermissionDeny } from '@/utils/function';
import { MAX_APPROVAL_LIST_SIZE } from '@/components/Approval';
import SVG from '@/components/SVG/SVG';
import SearchModal from '@/components/MobileApproval/SearchModal';
import { StyledSVGButton } from '@/styles/GlobalStyles';
import useScrollHeader from '@/components/MobileApproval/hooks/useScrollHeader';
import MyApproval from '@/components/MobileApproval/Body/MyApproval';
import MyWrite from '@/components/MobileApproval/Body/MyWrite';
import MyReferrer from '@/components/MobileApproval/Body/MyReferrer';
import TeamWrite from '@/components/MobileApproval/Body/TeamWrite';
import TeamApproval from '@/components/MobileApproval/Body/TeamApproval';
import FloatingButton from '@/components/FloatingButton';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { ATTENDANCE_MODAL_APPROVAL_TYPES } from '@/components/Approval/CreateApprovalModal/attendanceApproval';
import AttendanceApprovalForm from '@/components/MobileApproval/AttendanceApprovalForm';
import {
  APPROVAL_FILTER_QUERY_KEY,
  parseApprovalFilterMobileTab,
} from '@/utils/approval/approvalFilterLink';

export const MAX_LIST_SIZE = 20;

export const getApprovalkey =
  (url: string, obj: TotalApprovalParams) =>
  <T extends Record<string, unknown>>(
    pageIndex: number,
    previousPageData: T[],
  ) => {
    if (previousPageData && !previousPageData.length) {
      return null;
    }
    const newEntries = Object.entries(obj).map(([k, v]) => [
      k,
      typeof v === 'number' ? v.toString() : v,
    ]);
    const newObject = Object.fromEntries(newEntries) as Record<string, string>;

    const queryParam = new URLSearchParams(newObject);

    return `${url}?${queryParam}&currentPage=${pageIndex + 1}`;
  };
const MobileApproval = () => {
  const [open, setOpen] = React.useState(false);
  const { passportId } = useAtomValue(MyInfoState);
  const [selectedState, setSelectedState] = useAtom(approvalState);
  const [isInitial, setIsInitial] = useState(false);
  const { showHeader, scrollY } = useScrollHeader(50);
  const { t } = useTranslation();
  const [isRn, setIsRn] = useState(false);

  useEffect(() => {
    window?.isRNWebview && setIsRn(true);
  }, []);

  const { data: countData } = useSearchApproval(
    {
      listSize: MAX_APPROVAL_LIST_SIZE,
      sortType: 'authoredAt',
      orderType: 'desc',
      currentApproverPassportIds: [passportId],
    },
    {
      refreshInterval: isRn ? 1000 : 0,
      onError: (error, key) => {
        //RN에서는 jwtToken 만료를 정확하게 알수없어서 다음과 같이 세션만료
        if (isRn && error.response?.status === 401) {
          rnPermissionDeny(6);
        }
      },
    },
  );

  const { data: referrerCount } = useSearchApproval(
    {
      listSize: MAX_LIST_SIZE,
      currentPage: 1,
      sortType: 'authoredAt',
      orderType: 'desc',
      referrerPassportIds: [passportId],
      statuses: ['P', 'H', 'C'],
      unreadPassportIds: [passportId],
      isBookmark: false,
    },
    {
      refreshInterval: isRn ? 1000 : 0,
      onError: (error, key) => {
        //RN에서는 jwtToken 만료를 정확하게 알수없어서 다음과 같이 세션만료
        if (isRn && error.response?.status === 401) {
          rnPermissionDeny(7);
        }
      },
    },
  );

  const headerList = [
    {
      title: t('내 결재'),
      newData: countData && countData.meta.totalCount > 0,
    },
    { title: t('내 작성') },
    {
      title: t(`내 참조`),
      newData: referrerCount && referrerCount?.meta.totalCount > 0,
    },
    { title: t('팀 작성') },
    { title: t('팀 결재') },
  ];

  const handleClick = (id: number) => {
    sessionStorage.setItem(
      selectedState.headerKey.toString(),
      scrollY.toString(),
    );
    setSelectedState({ ...selectedState, headerKey: id });
  };

  useEffect(() => {
    window.scrollTo({
      top: parseInt(
        sessionStorage.getItem(selectedState.headerKey.toString()) || '0',
      ),
    });
  }, [selectedState.headerKey]);

  useEffect(() => {
    setIsInitial(true);
  }, []);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };
  const renderContent = () => {
    switch (selectedState.headerKey) {
      case 0:
        return <MyApproval showHeader={showHeader} />;
      case 1:
        return <MyWrite showHeader={showHeader} />;
      case 2:
        return <MyReferrer />;
      case 3:
        return <TeamWrite />;
      case 4:
      default:
        return <TeamApproval />;
    }
  };

  const [show, setShow] = useState(false);

  // 근태 양식 단독 진입(?write=<apv_type>) — 앱 네이티브 결재 탭에서 열린다.
  // 매핑에 없는 값은 무시하고 평소처럼 결재 목록을 보여준다.
  const router = useRouter();
  const writeParam = router.query.write;
  const writeFormType =
    typeof writeParam === 'string' &&
    ATTENDANCE_MODAL_APPROVAL_TYPES.includes(Number(writeParam))
      ? Number(writeParam)
      : null;

  // 결재 목록 딥링크(?approvalFilter=<slug>)로 들어오면 그 탭을 펴고 파라미터를 걷어낸다.
  // 결재 지연 알림처럼 PC 주소로 온 링크가 모바일에서 이 화면으로 넘어온다 (PORTAL-14212).
  // 적용은 진입당 1회 — 사용자가 다른 탭을 고른 뒤 다시 덮어쓰지 않게.
  const filterSlug = router.query[APPROVAL_FILTER_QUERY_KEY];
  const tabAppliedRef = useRef(false);
  useEffect(() => {
    if (!router.isReady || tabAppliedRef.current) return;
    if (filterSlug === undefined) return;

    tabAppliedRef.current = true;

    const tab = parseApprovalFilterMobileTab(filterSlug);
    // 모르는 slug 는 탭을 건드리지 않고 파라미터만 정리해 기본 동작을 유지한다.
    if (tab) {
      setSelectedState((prev) => ({
        ...prev,
        headerKey: tab.headerKey,
        myApprovalKey: tab.myApprovalKey,
      }));
    }

    const { [APPROVAL_FILTER_QUERY_KEY]: _, ...restQuery } = router.query;
    router.replace({ pathname: router.pathname, query: restQuery }, undefined, {
      shallow: true,
    });
  }, [router.isReady, filterSlug]);

  // 스크롤 감지 이벤트 추가
  useEffect(() => {
    const handleShowButton = () => {
      if (window.scrollY > 100) {
        setShow(true);
      } else {
        setShow(false);
      }
    };

    window.addEventListener('scroll', handleShowButton);
    return () => {
      window.removeEventListener('scroll', handleShowButton);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  // 정적 최적화 페이지라 첫 렌더에는 router.query 가 비어 있다. 준비되기 전에 그리면
  // ?write= 로 들어와도 결재 목록이 한 프레임 보였다가 모달로 바뀐다.
  if (!router.isReady) {
    return null;
  }

  // 딥링크 탭이 적용되기 전에 그리면 세션에 남아 있던 이전 탭(예: 팀 결재)이 한 프레임
  // 마운트되며 그 탭 조회가 한 번 나간다. 적용 후에 그린다.
  if (filterSlug !== undefined && !tabAppliedRef.current) {
    return null;
  }

  // ?write=<apv_type> 로 들어오면 그 근태 양식 모달만 띄운다.
  // (앱의 네이티브 결재 탭이 근태 양식을 이 URL 로 연다)
  if (writeFormType !== null) {
    return <AttendanceApprovalForm type={writeFormType} />;
  }

  return (
    <Container>
      <HeaderLayout showHeader={showHeader}>
        <Header
          title={'APPROVAL'}
          rightContent={
            <>
              <StyledSVGButton onClick={handleClickOpen}>
                <SVG
                  name="search2"
                  width={28}
                  height={28}
                  viewBox="0 0 28 28"
                  className="svg-icon"
                />
              </StyledSVGButton>
              <SearchModal open={open} handleClose={handleClose} />
            </>
          }
        />
        <HeaderContentContainer>
          {isInitial &&
            headerList.map((res, idx) => (
              <HeaderContent
                key={`headerList-${idx}`}
                id={idx}
                title={res.title}
                newData={res.newData}
                clicked={selectedState.headerKey === idx}
                handleClick={handleClick}
              />
            ))}
        </HeaderContentContainer>
      </HeaderLayout>
      <SubHeaderContentContainer>
        {isInitial && renderContent()}
      </SubHeaderContentContainer>
      <BannerLayout>
        <FloatingButton show={show} handleClick={scrollToTop}>
          <Image
            src={'/icons/arrow-up.svg'}
            alt={'scroll on Top'}
            width={14}
            height={14}
          />
        </FloatingButton>
        <ApprovalWirte />
      </BannerLayout>
    </Container>
  );
};

export default MobileApproval;
